/**
 * `/invitations/$id` — where the invitation mail's "Accept the invitation" button
 * lands (`clients/service.ts` `inviteMember`: `${deps.baseUrl}/invitations/${id}`).
 *
 * Outside `/app` on purpose. Everything under `/app` is inside the client shell and
 * the shell asks "which account am I looking at" before it draws a pixel; an invited
 * person is the one client-portal reader for whom that question has no answer yet.
 * So this route sits beside `/login`, is guarded by the same ladder, and renders a
 * `Splash` (CONTRACT.md §2.15).
 *
 * **`guardClientPortal`, not a rule of its own.** It is the client portal's door and
 * `auth/guards.ts` is the one place a destination is decided, so the two answers it
 * gives are exactly the two this route wants:
 *
 *   - **anonymous → `/login?redirect=/invitations/<id>`.** Not a courtesy: the read
 *     behind this page is `requirePrincipal()`-gated and then checked against the
 *     signed-in address, so there is literally nothing to render for a stranger.
 *     `/login` sends them back here with a session, and `location.href` is what
 *     carries the id across.
 *   - **staff → `/studio`.** A studio account can never hold a seat
 *     (`clients.service` refuses a staff address at `inviteMember` *and* at
 *     `acceptInvitation`, and `resolvers.ts` refuses to resolve one that does), so
 *     no invitation is ever addressed to them and this page could only tell them
 *     "no invitation is waiting". Their own portal is the better answer, and it is
 *     the one the guard already gives them for `/app`.
 */

import { createFileRoute } from '@tanstack/react-router'

import { guardClientPortal } from '../../auth/guards.js'
import { InvitationPage } from '../../pages/client/invitation/InvitationPage.js'

export const Route = createFileRoute('/invitations/$id')({
  beforeLoad: ({ context, location }) => {
    guardClientPortal(context.session.principal, location.href)
  },
  component: InvitationRoute,
})

function InvitationRoute() {
  const { id } = Route.useParams()
  return <InvitationPage id={id} />
}
